import { z } from "zod";

// ── Per-feature velocity ────────────────────────────────────

export const FeatureVelocity = z.object({
  featureId: z.string(),
  featureName: z.string(),
  category: z.string(),
  complexity: z.string(),

  // Time metrics
  wallClockDays: z.number(), // first session → last session
  activeHours: z.number(), // sum of session active time
  sessionCount: z.number(),

  // Token metrics
  totalInputTokens: z.number(),
  totalOutputTokens: z.number(),
  totalTokens: z.number(),
  cacheReadTokens: z.number(),

  // Cost metrics
  totalCost: z.number(),
  tokensPerActiveHour: z.number(),

  // Model usage
  modelMix: z.record(z.string(), z.number()), // model → % of tokens

  // Estimation quality
  estimatedTokens: z.number(),
  estimateAccuracy: z.number(), // 1.0 = perfect, >1 = overestimate, <1 = underestimate
  autoTagConfidence: z.number(), // average confidence of session tags
});
export type FeatureVelocity = z.infer<typeof FeatureVelocity>;

// ── Per-project velocity ────────────────────────────────────

export const ProjectVelocity = z.object({
  project: z.string(),
  generatedAt: z.string(),

  // Aggregate metrics
  featuresCompleted: z.number(),
  featuresInProgress: z.number(),
  featuresTotal: z.number(),

  // Throughput
  featuresPerWeek: z.number(),
  avgCostPerFeature: z.number(),
  avgTokensPerFeature: z.number(),
  avgSessionsPerFeature: z.number(),

  // Estimation quality
  overallAccuracy: z.number(), // mean abs error
  accuracyByComplexity: z.record(z.string(), z.number()),

  // Time metrics
  avgWallClockDays: z.number(),
  avgActiveHours: z.number(),
  activeToWallRatio: z.number(), // reveals context-switching

  // Total cost
  totalCost: z.number(),
  totalTokens: z.number(),

  // Per-feature breakdowns
  features: z.array(FeatureVelocity),
});
export type ProjectVelocity = z.infer<typeof ProjectVelocity>;

// ── Benchmark ───────────────────────────────────────────────

export const Benchmark = z.object({
  key: z.string(), // e.g. "M-react-component"
  complexity: z.string(),
  category: z.string(),
  dataPoints: z.number(),

  // Averages
  avgTokens: z.number(),
  avgCost: z.number(),
  avgSessions: z.number(),
  avgWallClockDays: z.number(),
  avgActiveHours: z.number(),

  // Ranges (p25 – p75)
  tokenRange: z.object({ p25: z.number(), p75: z.number() }),
  costRange: z.object({ p25: z.number(), p75: z.number() }),
  sessionRange: z.object({ p25: z.number(), p75: z.number() }),
});
export type Benchmark = z.infer<typeof Benchmark>;

// ── Input: what we need from tracking data ──────────────────

export const TrackedFeatureData = z.object({
  featureId: z.string(),
  featureName: z.string(),
  category: z.string(),
  complexity: z.string(),
  estimatedTokens: z.number(),
  status: z.string(),
  sessions: z.array(
    z.object({
      sessionId: z.string(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      cacheReadTokens: z.number(),
      model: z.string(),
      cost: z.number(),
      sessionStart: z.number(),
      sessionEnd: z.number().optional(),
      activeMinutes: z.number().optional(),
      confidence: z.number(),
    }),
  ),
});
export type TrackedFeatureData = z.infer<typeof TrackedFeatureData>;
